import { useEffect, useMemo, useState } from 'react'
import {
  Calendar,
  CreditCard,
  FileText,
  Lock,
  SquareCheckBig,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  useCompanyNotificationSettings,
  useNotificationSettings,
  useUpdateCompanyNotificationSettings,
  useUpdateNotificationSettings,
} from '@/hooks/useNotifications'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import {
  applyEventEnabled,
  eventEnabled,
  MANDATORY_NOTIFICATION_EVENTS,
  NOTIFICATION_CATEGORY_EVENTS,
  notificationPrefs,
  withMandatoryEventChannels,
} from '@/config/notificationEvents'
import type { NotificationChannelPrefs } from '@/types'

type NotificationGroupKey =
  | 'jobs'
  | 'quotes'
  | 'payments'
  | 'contracts'
  | 'tasks'
type NotificationSettingsMode = 'user' | 'company'

const GROUPS: {
  key: NotificationGroupKey
  icon: typeof Calendar
  iconClassName: string
}[] = [
  {
    key: 'jobs',
    icon: Calendar,
    iconClassName: 'bg-[#fff1ea] text-[#ff5a1f]',
  },
  {
    key: 'quotes',
    icon: FileText,
    iconClassName: 'bg-[#fff7df] text-[#c88719]',
  },
  {
    key: 'payments',
    icon: CreditCard,
    iconClassName: 'bg-[#ecfdf5] text-[#10b981]',
  },
  {
    key: 'contracts',
    icon: FileText,
    iconClassName: 'bg-[#fff7df] text-[#ff5a1f]',
  },
  {
    key: 'tasks',
    icon: SquareCheckBig,
    iconClassName: 'bg-[#e0f2fe] text-[#0284c7]',
  },
]

function activeCount(channels: Record<string, NotificationChannelPrefs>) {
  return Object.values(NOTIFICATION_CATEGORY_EVENTS)
    .flat()
    .filter(
      key =>
        MANDATORY_NOTIFICATION_EVENTS.has(key) ||
        eventEnabled(notificationPrefs(key, channels))
    ).length
}

function totalCount() {
  return Object.values(NOTIFICATION_CATEGORY_EVENTS).flat().length
}

interface NotificationEventsPanelProps {
  mode?: NotificationSettingsMode
}

export function NotificationEventsPanel({
  mode = 'company',
}: NotificationEventsPanelProps) {
  const { t } = useTranslation('settings')
  const userQuery = useNotificationSettings(mode === 'user')
  const companyQuery = useCompanyNotificationSettings(mode === 'company')
  const userUpdate = useUpdateNotificationSettings()
  const companyUpdate = useUpdateCompanyNotificationSettings()
  const query = mode === 'company' ? companyQuery : userQuery
  const { data, isLoading, isError } = query
  const [channels, setChannels] = useState<
    Record<string, NotificationChannelPrefs>
  >({})

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (data?.data?.channels) setChannels(data.data.channels)
  }, [data])

  const activated = useMemo(() => activeCount(channels), [channels])
  const total = totalCount()

  function setEventEnabled(key: string, value: boolean) {
    if (MANDATORY_NOTIFICATION_EVENTS.has(key)) return
    const nextChannels = applyEventEnabled(channels, key, value)
    setChannels(current => applyEventEnabled(current, key, value))
    const update = mode === 'company' ? companyUpdate : userUpdate
    update.mutate(
      { channels: withMandatoryEventChannels(nextChannels) },
      {
        onError: e =>
          toast({ variant: 'destructive', title: getApiErrorMessage(e) }),
      }
    )
  }

  if (isLoading) {
    return (
      <div className='rounded-2xl border border-[#e7e9ef] bg-white p-6 shadow-[0_12px_28px_-10px_rgba(38,45,66,0.14)]'>
        <Skeleton className='h-[520px] w-full' />
      </div>
    )
  }

  if (isError) {
    return (
      <div className='rounded-2xl border border-[#e7e9ef] bg-white p-6 shadow-[0_12px_28px_-10px_rgba(38,45,66,0.14)]'>
        <p className='py-12 text-center text-sm text-destructive'>
          {t('notifications.loadError')}
        </p>
      </div>
    )
  }

  return (
    <section className='relative overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_12px_28px_-10px_rgba(38,45,66,0.14),0_4px_10px_-6px_rgba(38,45,66,0.08)]'>
      <div className='p-6'>
        <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
          <div>
            <span
              className='absolute left-0 top-6 h-6 w-[3px] bg-[#ff5a1f]'
              aria-hidden='true'
            />
            <h1 className='font-heading text-sm font-semibold leading-[24px] text-[#171a20]'>
              {t(
                mode === 'company'
                  ? 'notifications.companyTitle'
                  : 'notifications.userTitle'
              )}
            </h1>
            <p className='mt-6 text-base font-medium leading-[1.4] text-[#3d434e]'>
              {t('notifications.summary', { active: activated, total })}
            </p>
          </div>
        </div>

        <div className='mt-6 rounded-xl border-l-2 border-[#d2d6e1] bg-[#f4f5f8] px-4 py-3 text-base font-medium leading-[1.4] text-[#6c7285]'>
          {t(
            mode === 'company'
              ? 'notifications.companyNotice'
              : 'notifications.userNotice'
          )}
        </div>

        <div className='mt-6 space-y-5'>
          {GROUPS.map(group => {
            const keys = NOTIFICATION_CATEGORY_EVENTS[group.key] ?? []
            const Icon = group.icon
            return (
              <article
                key={group.key}
                className='overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'
              >
                <header className='flex h-16 items-center justify-between border-b border-[#e7e9ef] bg-[#fbfbfc] px-5'>
                  <div className='flex items-center gap-3'>
                    <span
                      className={cn(
                        'flex size-6 items-center justify-center rounded-md p-1',
                        group.iconClassName
                      )}
                    >
                      <Icon className='size-3.5' aria-hidden='true' />
                    </span>
                    <h2 className='font-heading text-sm font-semibold lowercase leading-[1.2] text-[#171a20] first-letter:uppercase'>
                      {t(`notifications.groups.${group.key}`)}
                    </h2>
                  </div>
                  <span className='flex size-8 items-center justify-center rounded-full bg-[#f4f5f8] text-xs font-medium leading-4 text-[#3d434e]'>
                    {keys.length}
                  </span>
                </header>

                <div className='divide-y divide-[#e7e9ef]'>
                  {keys.map(key => {
                    const prefs = notificationPrefs(key, channels)
                    const locked = MANDATORY_NOTIFICATION_EVENTS.has(key)
                    const checked = locked || eventEnabled(prefs)
                    return (
                      <div
                        key={key}
                        className='flex min-h-20 items-center justify-between gap-4 px-5 py-3'
                      >
                        <div className='min-w-0'>
                          <div className='flex min-w-0 flex-wrap items-center gap-2'>
                            <p className='font-sans text-sm font-medium leading-[1.4] text-[#171a20]'>
                              {t(`notifications.events.${key}.title`)}
                            </p>
                            {locked && (
                              <span className='inline-flex h-6 items-center gap-1 rounded-full bg-[#fff7df] px-2 text-xs font-medium uppercase leading-4 text-[#a56300]'>
                                <Lock className='size-3.5' aria-hidden='true' />
                                {t('notifications.locked')}
                              </span>
                            )}
                          </div>
                          <p className='mt-1 text-xs font-normal leading-4 text-[#7b8191]'>
                            {t(`notifications.events.${key}.description`)}
                          </p>
                        </div>
                        <div className='flex shrink-0 items-center gap-3'>
                          <Switch
                            checked={checked}
                            disabled={locked}
                            onCheckedChange={value =>
                              setEventEnabled(key, value)
                            }
                            aria-label={t('notifications.ariaLabel.email', {
                              event: t(`notifications.events.${key}.title`),
                            })}
                          />
                          <span
                            className={cn(
                              'inline-flex h-7 min-w-[110px] items-center justify-center rounded-full px-3 text-xs font-medium uppercase leading-4',
                              checked
                                ? 'bg-[#f4f5f8] text-[#7b8191]'
                                : 'bg-[#f4f5f8] text-[#7b8191]'
                            )}
                          >
                            {checked
                              ? t('notifications.status.emailActivated')
                              : t('notifications.status.emailDeactivated')}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </article>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default NotificationEventsPanel
